import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MONEY_LABELS, MONEY_PROVENANCE, MONEY_TIPS } from '../../../../constants/deliveryMoney';
import { MaskedField } from '../../../../lib/access/MaskedField';
import { programRef } from '../../../../lib/access/types';
import { formatUtcYmd, parseUtcDate } from '../../_shared/api/nullable';
import { ConfirmDeleteDialog } from '../../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../../_shared/components/EntityFormDialog';
import { EntityTable } from '../../_shared/components/EntityTable';
import { FUNDED_NOT_EXERCISED } from '../../_shared/components/review/reviewItemCopy';
import { ScoreDetailDrawer } from '../../_shared/components/ScoreDetailDrawer';
import { formatMoney } from '../../_shared/lib/recordLabels';
import { usePeriodCloseouts } from '../closeoutHooks';
import { useProgram } from '../hooks';
import { popHooks, useOptionalTaskExercisesForProgram } from '../operationalHooks';
import { PROGRAM_WRITE_DENIED, useProgramWriteAccess } from '../programWriteAccess';
import { PeriodModificationHistory } from './PeriodModificationHistory';
// Values MUST match the pangaea chk_pop_period_type constraint
// (BASE/OPTION/EXTENSION/BRIDGE) — sending lowercase / option_period 500s
// every UpsertPeriodOfPerformance write.
const periodTypeOptions = [
    {
        value: 'BASE',
        label: 'Base'
    },
    {
        value: 'OPTION',
        label: 'Option Period'
    },
    {
        value: 'EXTENSION',
        label: 'Extension'
    },
    {
        value: 'BRIDGE',
        label: 'Bridge'
    }
];
// An option period funded with no exercise on file reads "Funded, not
// exercised" (D116): funding is not an exercise. It tells funded money is
// there, so a viewer who can't see the funded amount reads plain "No". The
// base period is exercised by definition, so it never reads that way.
function exercisedLabel(p, resource) {
    if (p.exercised) return 'Yes';
    if (p.fundedAmount <= 0 || !(p.periodType ?? '').toUpperCase().includes('OPTION')) return 'No';
    return /*#__PURE__*/ _jsx(MaskedField, {
        fieldName: "funded_amount",
        resource: resource,
        placeholder: "No",
        children: FUNDED_NOT_EXERCISED
    });
}
const popColumns = (resource)=>[
        {
            key: 'periodName',
            header: 'Period'
        },
        {
            key: 'periodType',
            header: 'Type'
        },
        {
            key: 'exercised',
            header: 'Exercised',
            render: (row)=>exercisedLabel(row, resource)
        },
        {
            key: 'startDate',
            header: 'Start',
            render: (row)=>row.startDate ? formatUtcYmd(row.startDate) ?? '—' : '—'
        },
        {
            key: 'endDate',
            header: 'End',
            render: (row)=>row.endDate ? formatUtcYmd(row.endDate) ?? '—' : '—'
        },
        {
            key: 'fundedAmount',
            header: MONEY_LABELS.funded,
            headerInfo: MONEY_TIPS.funded,
            render: (row)=>formatMoney(row.fundedAmount),
            maskAs: 'funded_amount'
        },
        {
            key: 'totalAmount',
            header: MONEY_LABELS.totalValue,
            headerInfo: MONEY_TIPS.totalValue,
            render: (row)=>formatMoney(row.totalAmount),
            maskAs: 'total_amount'
        }
    ];
const fields = [
    {
        kind: 'text',
        name: 'periodName',
        label: 'Period name',
        required: true,
        maxLength: 200
    },
    {
        kind: 'select',
        name: 'periodType',
        label: 'Type',
        required: true,
        options: periodTypeOptions
    },
    {
        kind: 'checkbox',
        name: 'exercised',
        label: 'Exercised'
    },
    // Not required: manually-added periods may legitimately lack dates.
    // No money fields: committed modifications are the only writer of
    // funded_amount / total_amount (D7) — the dialog's description says so.
    {
        kind: 'date',
        name: 'startDate',
        label: 'Start date'
    },
    {
        kind: 'date',
        name: 'endDate',
        label: 'End date'
    }
];
// Form defaults for a given record (null = create). Shared by the create modal
// and the drawer's inline edit so both surfaces start from the same shape.
const popDefaults = (p)=>({
        id: p?.id ?? '',
        periodName: p?.periodName ?? '',
        periodType: p?.periodType ?? '',
        optionNumber: p?.optionNumber ?? 0,
        exercised: p?.exercised ?? false,
        startDate: p?.startDate ? formatUtcYmd(p.startDate) ?? '' : '',
        endDate: p?.endDate ? formatUtcYmd(p.endDate) ?? '' : ''
    });
const ymd = (t)=>t ? formatUtcYmd(t) ?? '—' : '—';
// Read sections plus, per row, the inline-edit control. Every row the create
// form covers carries an `editField` (bound to the drawer's form) plus the
// option number, which the drawer surfaces in the Period section. Funded /
// total amounts wrap their READ value in MaskedField per-token (the two use
// different mask tokens, so the drawer's single-token mask can't cover both) —
// only a writer (canEdit) enters edit, and writers see full values, so the
// currency control is not a leak.
function popDetailSections(p, resource) {
    const currency = (n)=>formatMoney(n);
    return [
        {
            heading: 'Period',
            layout: 'columns',
            rows: [
                {
                    label: 'Period name',
                    value: p.periodName || '—',
                    editField: {
                        kind: 'text',
                        name: 'periodName',
                        label: 'Period name',
                        required: true,
                        maxLength: 200
                    }
                },
                {
                    label: 'Type',
                    value: periodTypeOptions.find((o)=>o.value === p.periodType)?.label ?? (p.periodType || '—'),
                    editField: {
                        kind: 'select',
                        name: 'periodType',
                        label: 'Type',
                        required: true,
                        options: periodTypeOptions
                    }
                }
            ]
        },
        {
            heading: 'Dates',
            layout: 'columns',
            rows: [
                {
                    label: 'Start',
                    value: ymd(p.startDate),
                    editField: {
                        kind: 'date',
                        name: 'startDate',
                        label: 'Start date'
                    }
                },
                {
                    label: 'End',
                    value: ymd(p.endDate),
                    editField: {
                        kind: 'date',
                        name: 'endDate',
                        label: 'End date'
                    }
                }
            ]
        },
        {
            heading: 'Value',
            layout: 'columns',
            // The money rows carry no editField ON PURPOSE (D7): committed
            // modifications are the only writer of funded_amount / total_amount, so
            // in edit mode these stay read-only figures while dates/name edit. The
            // MONEY_TIPS copy on each label is the pointer to where money comes from.
            rows: [
                {
                    label: MONEY_LABELS.funded,
                    labelInfo: MONEY_TIPS.funded,
                    value: /*#__PURE__*/ _jsx(MaskedField, {
                        fieldName: "funded_amount",
                        resource: resource,
                        children: currency(p.fundedAmount)
                    })
                },
                {
                    label: MONEY_LABELS.totalValue,
                    labelInfo: MONEY_TIPS.totalValue,
                    value: /*#__PURE__*/ _jsx(MaskedField, {
                        fieldName: "total_amount",
                        resource: resource,
                        children: currency(p.totalAmount)
                    })
                },
                {
                    label: 'Exercised',
                    value: exercisedLabel(p, resource),
                    editField: {
                        kind: 'checkbox',
                        name: 'exercised',
                        label: 'Exercised'
                    }
                }
            ]
        }
    ];
}
// Where a printed funded amount was read (delivery.programs.funding_basis).
const PRINTED_FUNDING_BASIS = {
    award_block_26: "as printed in the award's Block 26",
    award_obligation_statement: "as the award's obligation statements print it",
    modification_obligation: 'as the latest modification restates it',
    source_aggregate: 'as the documents state it'
};
export function PeriodsOfPerformanceTab({ programId }) {
    // Upsert/DeletePeriodOfPerformance: update + area:execution. canEdit is
    // the update verb alone (same asymmetry SAD-73 named on Capabilities).
    const canWrite = useProgramWriteAccess(programId).execution;
    const { data, isLoading } = popHooks.useList({
        programId
    });
    const { data: program } = useProgram(programId);
    const { data: exercises, isError: exercisesFailed } = useOptionalTaskExercisesForProgram(programId);
    const upsert = popHooks.useUpsert();
    const remove = popHooks.useDelete();
    const { data: periodCloseouts } = usePeriodCloseouts(programId);
    const items = data?.items ?? [];
    const resource = programRef(programId);
    const [formOpen, setFormOpen] = useState(false);
    const [deleting, setDeleting] = useState(null);
    // Store only the selected id and DERIVE the record from `items` — so an open
    // drawer always reflects the latest list (e.g. after an inline-edit Save)
    // without an effect that re-syncs state (which infinite-loops when the list
    // returns fresh-but-equal references each render).
    const [detailId, setDetailId] = useState(null);
    const detail = detailId ? items.find((p)=>p.id === detailId) ?? null : null;
    // Whether the detail drawer opens in read or straight into edit (row Edit).
    const [detailMode, setDetailMode] = useState('read');
    // Shared by the create modal and drawer edit-in-place; the target id is passed
    // in (empty = create). The upsert is full-replace, so every field is sent.
    const submitPoP = async (values, id)=>{
        // YMD strings compare lexicographically; the throw renders inline.
        if (values.startDate && values.endDate && values.endDate < values.startDate) {
            throw new Error('End date must be on or after start date');
        }
        const src = id ? items.find((p)=>p.id === id) ?? null : null;
        await upsert.mutateAsync({
            id,
            programId,
            periodName: values.periodName,
            periodType: values.periodType,
            // Option # is seeded from the record and surfaced in the drawer's Period
            // section, so an untouched edit re-sends whatever a sync import assigned
            // (baltigo numbers imported option periods 1, 2, …) instead of NULLing it
            // to 0. New manual periods default to 0.
            optionNumber: Math.trunc(Number(values.optionNumber) || 0),
            exercised: Boolean(values.exercised),
            // No money is sent at all (D7: mods are the only writer). The server is
            // the rail, twice over: ryugu ignores request money (stored figures ride
            // through updates, creates start unfunded) and pangaea's generic Update
            // omits the money columns — so the full-replace shape can't zero them
            // (caveat C4) no matter what a client sends.
            // Always sent: the handler is full-replace, so omitting dates on an
            // imported period would NULL them (the wipe this branch fixes).
            startDate: values.startDate ? parseUtcDate(values.startDate) : undefined,
            endDate: values.endDate ? parseUtcDate(values.endDate) : undefined,
            sortOrder: src ? src.sortOrder : Math.max(0, ...items.map((p)=>p.sortOrder).filter(Number.isFinite)) + 1
        });
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    // The drawer's inline-edit config, bound to the viewed record.
    const detailEdit = detail ? {
        defaultValues: popDefaults(detail),
        onSubmit: (v)=>submitPoP(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes'
    } : undefined;
    // SAD-885: the contract's funded amount is the obligated total the
    // documents print; a period carries only what its lines fund. What no
    // period carries is said here, so a $0.00 period never reads as unfunded.
    // SAD-886: money a modification puts on an optional-task exercise (its
    // stated cost) is assigned too, to the task, not left over.
    const fundedBasis = program?.program?.fundingBasis ?? '';
    const contractFunded = program?.program?.fundedAmount ?? 0;
    const onTasks = (exercises ?? []).filter((e)=>e.costIsNull !== true).reduce((sum, e)=>sum + e.cost, 0);
    const unassigned = contractFunded - items.reduce((sum, p)=>sum + p.fundedAmount, 0) - onTasks;
    const printedFrom = PRINTED_FUNDING_BASIS[fundedBasis];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-4",
                children: [
                    printedFrom && unassigned > 0.005 && !exercisesFailed ? /*#__PURE__*/ _jsxs("p", {
                        className: "text-sm text-muted-foreground",
                        "data-testid": Testids.deliveryOperational.popContractFunded,
                        children: [
                            "Contract funded",
                            ' ',
                            /*#__PURE__*/ _jsx(MaskedField, {
                                fieldName: "funded_amount",
                                resource: resource,
                                children: /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium text-foreground tabular-nums",
                                    children: formatMoney(contractFunded)
                                })
                            }),
                            ", ",
                            printedFrom,
                            ".",
                            ' ',
                            unassigned >= contractFunded - 0.005 ? 'The documents assign none of it to a period.' : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(MaskedField, {
                                        fieldName: "funded_amount",
                                        resource: resource,
                                        children: formatMoney(unassigned)
                                    }),
                                    ' ',
                                    "of it is not assigned to a period",
                                    onTasks > 0.005 ? ' or an optional task' : '',
                                    " by the documents."
                                ]
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx("span", {}),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>setFormOpen(true),
                        disabled: !canWrite,
                        title: !canWrite ? PROGRAM_WRITE_DENIED.execution : undefined,
                        "data-testid": Testids.deliveryOperational.popCreate,
                        children: "Add Period"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ClosedPeriodsBanner, {
                programId: programId,
                periods: items,
                closeouts: periodCloseouts ?? []
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: popColumns(resource),
                testid: Testids.deliveryOperational.popTable,
                rowTestid: Testids.deliveryOperational.popRow,
                rowKey: (p)=>p.id,
                isLoading: isLoading,
                hideRowActions: !canWrite,
                resource: resource,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                // Edit-in-place: open the drawer straight into edit mode.
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryOperational.popEdit,
                                children: "Edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryOperational.popDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Add Period of Performance",
                description: MONEY_PROVENANCE,
                fields: fields,
                defaultValues: popDefaults(null),
                onSubmit: async (v)=>{
                    await submitPoP(v, '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Period",
                description: deleting ? `Delete period "${deleting.periodName}"? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? detail.periodName || 'Period of Performance' : '',
                sections: detail ? popDetailSections(detail, resource) : [],
                testid: Testids.deliveryOperational.popDetailDrawer,
                edit: detailEdit,
                canEdit: canWrite,
                initialMode: detailMode,
                children: detail && /*#__PURE__*/ _jsx(PeriodModificationHistory, {
                    period: detail,
                    programId: programId,
                    resource: resource
                })
            })
        ]
    });
}
// Closed contract periods, each linking to its closeout. Closing a period
// makes only its checklist answers read-mostly; the period's own records stay
// editable here.
function ClosedPeriodsBanner({ programId, periods, closeouts }) {
    const closed = periods.flatMap((p)=>{
        const c = closeouts.find((x)=>x.periodId === p.id && x.closedAt);
        return c ? [
            {
                period: p,
                closeout: c
            }
        ] : [];
    });
    if (closed.length === 0) return null;
    return /*#__PURE__*/ _jsx("div", {
        className: "rounded-md border bg-muted/50 p-3 text-sm text-muted-foreground",
        "data-testid": Testids.deliveryOperational.popClosedBanner,
        children: closed.map(({ period, closeout })=>/*#__PURE__*/ _jsxs("p", {
                "data-period-id": period.id,
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium text-foreground",
                        children: period.periodName || 'Period'
                    }),
                    " is closed",
                    closeout.closedAt ? ` (${formatUtcYmd(closeout.closedAt) ?? ''})` : '',
                    ".",
                    ' ',
                    /*#__PURE__*/ _jsx(Link, {
                        to: `/delivery/programs/${programId}/closeout?period=${encodeURIComponent(period.id)}`,
                        className: "text-primary underline",
                        children: "View its closeout"
                    })
                ]
            }, period.id))
    });
}
